import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { AccountCompanyDetails } from '../../../../models/account-company-details.model';
import { AccountCompany } from '../../../../models/account-company.model';
import { CompanyService } from '../../../../services/company.service';
import { ILookup, FullPageMaskService, ErrorReportingService } from 'common';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'acc-company-details',
  templateUrl: './company-details.component.html',
  styleUrls: ['./company-details.component.scss'],
})
export class CompanyDetailsComponent implements OnInit, OnDestroy {
  @Input() organisationTypes: ILookup[];
  @Input() company: AccountCompany;
  @Input() canEdit: boolean;
  @Output() isEditing = new EventEmitter<string>();

  group: UntypedFormGroup;
  isError = false;
  errorMessages = [];

  private regUrl = '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?';
  private _destroy$ = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private errorService: ErrorReportingService,
    private maskService: FullPageMaskService
  ) {}

  ngOnInit(): void {
    this.createForm(this.company.details);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  edit(formGroup: UntypedFormGroup) {
    formGroup.enable();
    this.isError = false;
    this.isEditing.emit('company');
  }

  cancel(formGroup: UntypedFormGroup) {
    this.isError = false;
    formGroup.reset(this.company.details);
    formGroup.disable();
    this.isEditing.emit('');
  }

  async save(data: AccountCompanyDetails) {
    if (!this.group.valid) {
      return;
    }

    const company: AccountCompany = this.company.getDeepCopy();

    company.details = data;
    this.updateOrganisationType(company.details);

    this.maskService.showLoading();
    this.isError = false;

    this.companyService
      .updateCompany(company)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (wasRequestOk: boolean) => {
          this.isError = !wasRequestOk;
          if (wasRequestOk) {
            this.showStatus();
            this.company = company;
            this.group.disable();
            this.isEditing.emit('');
            this.maskService.hideLoading();
          }
        },
        error: (errorPayload) => {
          this.errorMessages = this.errorService.getResponseDTOErrors(errorPayload);
          this.isError = true;
          this.maskService.hideLoading();
        },
      });
  }

  showStatus(): void {
    this.errorService.displayInfo('The company details were updated successfully');
  }

  createForm(data: AccountCompanyDetails) {
    this.group = new UntypedFormGroup({
      acCode: new UntypedFormControl(data.acCode),
      providerType: new UntypedFormControl(data.providerType),
      name: new UntypedFormControl(data.name),
      officialName: new UntypedFormControl(data.officialName),
      organisationTypeString: new UntypedFormControl(data.organisationTypeString),
      currentTradingName: new UntypedFormControl(data.currentTradingName),
      previousTradingName: new UntypedFormControl(data.previousTradingName),
      registrationNumber: new UntypedFormControl(data.registrationNumber),
      website: new UntypedFormControl(data.website, Validators.pattern(this.regUrl)),
      deliveryMethod: new UntypedFormControl(data.deliveryMethod.toString()),
      languagePreference: new UntypedFormControl(data.languagePreference.toString()),
      isMobileOperator: new UntypedFormControl(data.isMobileOperator),
      isFixedOperator: new UntypedFormControl(data.isFixedOperator),
    });

    this.group.disable();
  }

  private updateOrganisationType(data: AccountCompanyDetails) {
    const organisationTypeString = data.organisationTypeString;
    const orgTypeLookup = this.findOrganisationTypeByName(organisationTypeString);
    if (orgTypeLookup) {
      data.organisationTypeId = +orgTypeLookup.key;
      this.company.details.organisationTypeString = orgTypeLookup.value;
    } else {
      data.organisationTypeId = null;
      this.company.details.organisationTypeString = null;
    }
  }

  private findOrganisationTypeByName(name: string): ILookup {
    if (typeof name === 'undefined' || name === null || name === '') {
      return null;
    }
    for (const i in this.organisationTypes) {
      if (this.organisationTypes[i].value === name) {
        return this.organisationTypes[i];
      }
    }
    return null;
  }
}
